import { useEffect, useState, useCallback, useRef } from 'react';
import { BFF_URL } from '../lib/runtimeEnv.js';
import { onTransportFailure } from '@parsina/framework/clinical/src/lib/connectivitySignal';

// ─────────────────────────────────────────────────────────────────────────────
// Is the app actually able to reach its backend?
//
// The app had no answer to this at all. A doctor whose Wi-Fi dropped mid-clinic
// saw each screen fail on its own schedule, each with its own error card, none
// of them saying the one thing that explained all of them — and nothing told
// them when it came back, so the habit became "refresh and hope".
//
// `navigator.onLine` alone is not the answer, and the direction of its error
// matters. It is RELIABLE when false (the OS says there is no route to
// anywhere) and near-worthless when true (it means "an interface is up", which
// a captive portal, an expired hotel Wi-Fi login, a VPN drop and a firewall all
// satisfy while dropping every packet to the BFF). So:
//
//   offline  — the browser says so. Trusted immediately, no probe needed.
//   degraded — the browser says we are online but the BFF is not answering.
//              Only entered from real evidence: a request that actually failed,
//              confirmed by a probe. This is the state that catches the captive
//              portal, and it is why a bare `navigator.onLine` hook was not
//              enough.
//   online   — reachable.
//
// The probe is deliberately cheap and deliberately rare: it only runs while
// something is known to be wrong, or once when the browser says the network
// came back. It never polls a healthy connection — a heartbeat that fires all
// day to detect a condition that lasts seconds is a battery cost paid by every
// doctor to benefit none of them.
// ─────────────────────────────────────────────────────────────────────────────

/** The browser's own verdict. Reliable when true, worthless when false — see above. */
const isBrowserOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

export const ConnectionState = {
  ONLINE: 'online',
  DEGRADED: 'degraded',
  OFFLINE: 'offline',
};

// Backoff for the recovery probe while disconnected. Starts brisk — most
// outages are a few seconds of a lift or a dead spot, and the doctor should not
// wait a minute to be told they are back — then eases off so a genuinely long
// outage is not a request every two seconds for twenty minutes.
const PROBE_MIN_MS = 3_000;
const PROBE_MAX_MS = 30_000;

// The probe budget. Much shorter than a real query's: this asks one question
// ("is anything there?") and a slow answer is a no for our purposes.
const PROBE_TIMEOUT_MS = 5_000;

/**
 * Ask the BFF whether it is reachable.
 *
 * Sends the smallest legal GraphQL request rather than hitting a health path:
 * the BFF has no unauthenticated health endpoint, and this way the probe
 * traverses exactly the path real queries do — same origin, same proxy, same
 * TLS. Any HTTP response at all counts as reachable, INCLUDING 400 and 401: the
 * question is whether packets arrive, not whether we are welcome. Treating a
 * 401 as "offline" would show a network banner to a doctor whose session simply
 * expired, which is Phase 4's job and a different conversation.
 */
async function probeBff(signal) {
  try {
    const res = await fetch(BFF_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: '{__typename}' }),
      signal,
      cache: 'no-store',
    });
    return Boolean(res);
  } catch {
    return false;
  }
}

export function useOnlineStatus() {
  const [state, setState] = useState(() =>
    isBrowserOffline() ? ConnectionState.OFFLINE : ConnectionState.ONLINE);

  // Was the connection ever lost in this session? Drives the transient «اتصال
  // برقرار شد» confirmation — without it, the banner would announce a recovery
  // to every doctor who simply opened the app.
  const [recovered, setRecovered] = useState(false);

  const timerRef = useRef(null);
  const backoffRef = useRef(PROBE_MIN_MS);
  const abortRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  const goOnline = useCallback(() => {
    backoffRef.current = PROBE_MIN_MS;
    clearTimer();
    setState((prev) => {
      if (prev !== ConnectionState.ONLINE) setRecovered(true);
      return ConnectionState.ONLINE;
    });
  }, []);

  /**
   * Run one probe now and schedule the next if it fails.
   *
   * Exposed as `check` so a failed real request can hand its evidence over
   * rather than making the hook discover the same outage independently.
   */
  const check = useCallback(async () => {
    if (isBrowserOffline()) {
      setState(ConnectionState.OFFLINE);
      return false;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

    const reachable = await probeBff(controller.signal);
    clearTimeout(timeout);

    if (reachable) {
      goOnline();
      return true;
    }

    // Re-read the authoritative signal at the moment of the verdict rather than
    // trusting the state we entered with: a probe can outlive the condition that
    // started it, and an in-flight request that resolves after the interface
    // genuinely dropped must not downgrade a true OFFLINE to DEGRADED.
    //
    // The reverse matters more, and is the case the test pins: once the browser
    // reports online again, a failing probe means DEGRADED — there IS a network,
    // the BFF is just not on the other end of it. Keeping OFFLINE sticky here
    // would have described a captive portal as "no internet", which sends the
    // doctor to look at a router that is working perfectly.
    setState(isBrowserOffline() ? ConnectionState.OFFLINE : ConnectionState.DEGRADED);
    clearTimer();
    timerRef.current = setTimeout(check, backoffRef.current);
    backoffRef.current = Math.min(backoffRef.current * 2, PROBE_MAX_MS);
    return false;
  }, [goOnline]);

  /**
   * Report a request that just failed for transport reasons.
   *
   * The distinction that makes this hook trustworthy: we do NOT go straight to
   * `degraded` on one failed request. A single 502 from one flaky upstream is
   * not a connectivity problem, and telling the doctor their connection is down
   * when one panel failed would train them to ignore the banner — which is the
   * whole asset here. The probe is what turns a suspicion into a claim.
   */
  const reportFailure = useCallback(() => {
    if (stateRef.current === ConnectionState.ONLINE) check();
  }, [check]);

  useEffect(() => {
    const handleOffline = () => {
      // No probe: the browser is authoritative in this direction, and firing a
      // request at a machine with no route just burns a timeout.
      clearTimer();
      abortRef.current?.abort();
      setState(ConnectionState.OFFLINE);
    };

    // …and NOT authoritative in the other. 'online' means an interface came up,
    // which a captive portal satisfies. Confirm before claiming recovery.
    const handleOnline = () => { backoffRef.current = PROBE_MIN_MS; check(); };

    // Coming back to a backgrounded tab is the other moment worth checking: a
    // laptop that slept through a network change reports nothing, and the first
    // thing the doctor does is read a screen full of stale data.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && stateRef.current !== ConnectionState.ONLINE) check();
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    // The evidence `navigator.onLine` cannot give us: a real request that just
    // failed at the transport layer.
    const unsubscribe = onTransportFailure(reportFailure);

    // A tab that BOOTS offline never fires an event — it was already in that
    // state — so the initial value has to stand on its own, and it does.
    if (isBrowserOffline()) setState(ConnectionState.OFFLINE);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
      unsubscribe();
      clearTimer();
      abortRef.current?.abort();
    };
  }, [check, reportFailure]);

  return {
    state,
    isOnline: state === ConnectionState.ONLINE,
    isOffline: state === ConnectionState.OFFLINE,
    isDegraded: state === ConnectionState.DEGRADED,
    recovered,
    acknowledgeRecovery: useCallback(() => setRecovered(false), []),
    check,
    reportFailure,
  };
}

export default useOnlineStatus;
